<script setup lang="ts">
import { moment } from "@/utils/useTimeZone";
import { giftVoucherDontShowValidator } from "@core/utils/validators";

// Interface
interface Emit {
  (e: "closeDialog", value?: boolean): void;
  (e: "editGiftVoucher", value?: any): void;
}

interface Props {
  isDialogOpen: boolean;
  isEditingGiftVoucher: boolean;
  selectedGiftVoucher: any;
}

// Props
const props = defineProps<Props>();

// Emits
const emit = defineEmits<Emit>();

// Reactive State
const giftVoucherForm = ref<any>();

const giftVoucherDetail = ref<any>({
  id: "",
  to: "",
  from: "",
  message: "",
  originalAmount: "",
  balanceRemaining: "",
  email: "",
  expiry: "",
  emailed: false,
  printed: false,
  labelPrinted: false,
  dontShowValue: false,
});

const setGiftVoucherDetail = (data: any) => {
  giftVoucherDetail.value = {
    ...data,
    dontShowValue: !data?.dontShowValue,
    expiry:
      data.expiry && moment().isValid()
        ? moment(data.expiry).format("DD-MM-YYYY")
        : "",
  };
};

const sendGiftVoucherDetail = async () => {
  const { valid } = await giftVoucherForm.value?.validate();
  if (!valid) return;

  emit("editGiftVoucher", {
    ...giftVoucherDetail.value,
    dontShowValue: !giftVoucherDetail.value?.dontShowValue,
    expiry: props?.selectedGiftVoucher?.expiry,
  });
};

watch(
  () => props.isDialogOpen,
  (newVal) => {
    if (newVal) setGiftVoucherDetail(props.selectedGiftVoucher);
  }
);
</script>

<template>
  <div>
    <VDialog
      :model-value="props.isDialogOpen"
      persistent
      max-width="800"
      no-click-animation
    >
      <VCard>
        <div
          class="d-flex align-center justify-space-between bg-dialogHeaderColor"
        >
          <VCardTitle>{{
            props.isEditingGiftVoucher ? "Save Gift Voucher" : "Gift Voucher Detail"
          }}</VCardTitle>
          <VBtn
            icon="mdi-close"
            size="large"
            variant="text"
            class="rounded-0"
            color=""
            @click="emit('closeDialog')"
          />
        </div>

        <VCardText class="mt-2">
          <VForm ref="giftVoucherForm">
            <VRow>
              <!-- Code  -->
              <VCol cols="12" sm="4">
                <VTextField v-model="giftVoucherDetail.id" readonly label="Code" />
              </VCol>
              <!-- To -->
              <VCol cols="12" sm="4">
                <VTextField
                  v-model="giftVoucherDetail.to"
                  :readonly="!props.isEditingGiftVoucher"
                  label="To"
                />
              </VCol>
              <!-- From -->
              <VCol cols="12" sm="4">
                <VTextField
                  v-model="giftVoucherDetail.from"
                  :readonly="!props.isEditingGiftVoucher"
                  label="From"
                />
              </VCol>
            </VRow>

            <VRow>
              <!-- Message -->
              <VCol cols="12">
                <VTextarea
                  v-model="giftVoucherDetail.message"
                  :readonly="!props.isEditingGiftVoucher"
                  :rules="[
                    (value: string) =>
                      giftVoucherDontShowValidator(
                        giftVoucherDetail.dontShowValue,
                        value,
                      ),
                  ]"
                  label="Message"
                  rows="1"
                />
              </VCol>
            </VRow>

            <VRow>
              <!-- Original Amount -->
              <VCol cols="12" sm="6">
                <VTextField
                  v-model="giftVoucherDetail.originalAmount"
                  readonly
                  label="Original Amount"
                  prefix="$"
                />
              </VCol>
              <!-- Balance Remaining -->
              <VCol cols="12" sm="6">
                <VTextField
                  v-model="giftVoucherDetail.balanceRemaining"
                  readonly
                  label="Balance Remaining"
                  prefix="$"
                />
              </VCol>
            </VRow>

            <VRow>
              <!-- Expired -->
              <VCol cols="12" sm="6">
                <VTextField
                  v-model="giftVoucherDetail.expiry"
                  readonly
                  label="Expiry"
                />
              </VCol>
              <!-- Email -->
              <VCol cols="12" sm="6">
                <VTextField
                  v-model="giftVoucherDetail.email"
                  readonly
                  label="Email"
                />
              </VCol>
            </VRow>

            <VRow>
              <!-- Emailed -->
              <VCol cols="6" sm="3">
                <VCheckbox
                  v-model="giftVoucherDetail.emailed"
                  readonly
                  label="Emailed"
                />
              </VCol>
              <!-- Printed -->
              <VCol cols="6" sm="3">
                <VCheckbox
                  v-model="giftVoucherDetail.printed"
                  readonly
                  label="Printed"
                />
              </VCol>
              <!-- Label Printed -->
              <VCol cols="6" sm="3">
                <VCheckbox
                  v-model="giftVoucherDetail.labelPrinted"
                  readonly
                  label="Label Printed"
                />
              </VCol>
              <!-- Dont Show Value -->
              <VCol cols="6" sm="3">
                <VCheckbox
                  v-model="giftVoucherDetail.dontShowValue"
                  :readonly="!props.isEditingGiftVoucher"
                  label="Show Value on Card"
                />
              </VCol>
            </VRow>
          </VForm>
        </VCardText>

        <VCardActions>
          <VSpacer />
          <VBtn
            v-if="props.isEditingGiftVoucher"
            color="success"
            variant="flat"
            @click="sendGiftVoucherDetail"
          >
            Save
          </VBtn>
          <VBtn color="error" variant="flat" @click="emit('closeDialog')">
            Close
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
